import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function MissionVision() {
  const { t } = useTranslation();
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const items = [
    {
      icon: 'ri-focus-3-line',
      label: t('homeSections.missionVision.cards.mission.label'),
      text: t('homeSections.missionVision.cards.mission.text'),
    },
    {
      icon: 'ri-eye-line',
      label: t('homeSections.missionVision.cards.vision.label'),
      text: t('homeSections.missionVision.cards.vision.text'),
    },
    {
      icon: 'ri-heart-line',
      label: t('homeSections.missionVision.cards.values.label'),
      text: t('homeSections.missionVision.cards.values.text'),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.15 }
    );
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="missao-visao" className="py-28 px-6 bg-gradient-to-b from-[#08111F] to-[#0F1F38] relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-[#C9A84C]/10 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#C9A84C]/10 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A84C]/40 bg-[#C9A84C]/10 mb-6">
            <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">{t('homeSections.missionVision.badge')}</span>
          </div>
          <h2 className="text-5xl font-extrabold text-white leading-tight mb-4">
            {t('homeSections.missionVision.titleBefore')} <span className="text-[#C9A84C]">{t('homeSections.missionVision.titleHighlight')}</span>
          </h2>
          <div className="w-20 h-0.5 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-white/90 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('homeSections.missionVision.description')}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item, index) => (
            <div
              key={index}
              ref={(el) => { cardRefs.current[index] = el; }}
              className="opacity-0 translate-y-8 transition-all duration-700"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-10 h-full border border-white/10 hover:border-[#C9A84C]/50 hover:shadow-[0_20px_60px_rgba(201,168,76,0.12)] transition-all duration-500 flex flex-col items-start">
                {/* Icon circle */}
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A84C]/20 border border-[#C9A84C]/30 mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <i className={`${item.icon} text-2xl text-[#E8D4A0]`}></i>
                </div>

                {/* Label */}
                <h3 className="text-[#C9A84C] font-extrabold text-sm tracking-[0.2em] uppercase mb-4">
                  {item.label}
                </h3>

                {/* Divider */}
                <div className="w-10 h-0.5 bg-[#C9A84C]/40 mb-5"></div>

                {/* Text */}
                <p className="text-white/90 text-base leading-relaxed">
                  {item.text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom image strip */}
        <div className="mt-20 rounded-2xl overflow-hidden relative h-64 shadow-xl">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20travel%20aerial%20view%20of%20turquoise%20ocean%20with%20tropical%20island%20white%20sand%20beach%20crystal%20clear%20water%20maldives%20style%20golden%20hour%20light%20cinematic%20photography%20high%20end%20travel%20magazine%20cover%20vibrant%20colors%20breathtaking%20scenery%20paradise%20destination&width=1200&height=400&seq=mv-banner-2026&orientation=landscape"
            alt={t('homeSections.missionVision.imageAlt')}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/70 via-[#0A1628]/30 to-transparent flex items-center px-12">
            <div>
              <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-2">Aero Destiny Travel</p>
              <h4 className="text-white text-3xl font-extrabold leading-tight max-w-md">
                {t('homeSections.missionVision.imageTitleBefore')} <span className="text-[#C9A84C]">{t('homeSections.missionVision.imageTitleHighlight')}</span>
              </h4>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
